import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Wrench } from "lucide-react";

import { ApiError } from "../../lib/api";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Button, Select, Textarea } from "../../components/ui";
import { crearOrden, type PrioridadOT } from "../mantenimiento/api";
import type { ConsumoAnomalo } from "./api";

const MESES_CORTOS = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
];

/** Texto inicial de la OT: el hallazgo, ya redactado. */
function descripcionPorDefecto(a: ConsumoAnomalo): string {
  const periodo = `${MESES_CORTOS[a.periodo_mes - 1]} ${a.periodo_anio}`;
  const donde =
    `${a.socio_nombre}` +
    (a.arranque_codigo ? ` · ${a.arranque_codigo}` : "") +
    (a.arranque_direccion ? ` · ${a.arranque_direccion}` : "");
  const medidor = a.medidor_numero_serie
    ? ` Medidor N° ${a.medidor_numero_serie}.`
    : "";
  if (a.patron_cero) {
    return (
      `Revisar medidor: ${a.meses_en_cero ?? 0} mes(es) en cero ` +
      `(patrón ${a.patron_cero}) hasta ${periodo}.${medidor}\n${donde}`
    );
  }
  return (
    `Revisar posible fuga: ${a.consumo_actual} m³ en ${periodo} contra un ` +
    `promedio de ${a.promedio_previo ?? "—"} m³` +
    (a.factor != null ? ` (×${a.factor})` : "") +
    `.${medidor}\n${donde}`
  );
}

/**
 * Deriva un hallazgo de la revisión de consumos a una orden de trabajo
 * (recorrido R1). La OT queda enlazada al medidor, así que la hoja del
 * medidor cuenta después por qué se fue a revisar.
 */
export function CrearOtRevisionModal({
  anomalia,
  onClose,
}: {
  anomalia: ConsumoAnomalo;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const [descripcion, setDescripcion] = useState(descripcionPorDefecto(anomalia));
  const [prioridad, setPrioridad] = useState<PrioridadOT>(
    anomalia.patron_cero === "detenido" ? "Alta" : "Media",
  );

  const m = useMutation({
    mutationFn: () =>
      crearOrden({
        tipo: "Correctivo",
        prioridad,
        descripcion_solicitud: descripcion.trim(),
        medidor_id: anomalia.medidor_id,
      }),
    onSuccess: (ot) => {
      toast.success(
        `OT ${ot.anio}-${String(ot.numero).padStart(4, "0")} creada para revisar el medidor.`,
      );
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo crear la OT"),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Crear OT de revisión"
      subtitle={anomalia.socio_nombre}
      icon={Wrench}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={descripcion.trim().length < 5 || m.isPending}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Creando…" : "Crear orden de trabajo"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          Queda como OT correctiva pendiente en Mantenimiento, lista para
          asignarle un técnico.
          {anomalia.medidor_id == null && (
            <>
              {" "}
              <span className="text-amber-700">
                Este arranque no tiene medidor instalado, así que la OT queda sin
                enlace al medidor.
              </span>
            </>
          )}
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">Prioridad</span>
          <Select
            value={prioridad}
            onChange={(e) => setPrioridad(e.target.value as PrioridadOT)}
            className="mt-1"
          >
            <option value="Baja">Baja</option>
            <option value="Media">Media</option>
            <option value="Alta">Alta</option>
            <option value="Critica">Crítica</option>
          </Select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Qué hay que revisar
          </span>
          <Textarea
            rows={4}
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="mt-1"
          />
        </label>
      </div>
    </Modal>
  );
}
